import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Download, Description, Assignment } from '@mui/icons-material';
import { clientApi, filingApi } from '../services/api';

const Reports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const downloadClientsReport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await clientApi.getAll();
      const clients = response.data.data || [];

      // Convert to CSV
      const headers = [
        'Client Name',
        'Email',
        'HST Frequency',
        'Period Start',
        'Period End',
        'Due Date',
        'Status',
        'Employee Assigned',
        'Employee Email',
      ];

      const rows = clients.map((client: any) => [
        client.clientName,
        client.clientEmail,
        client.hstFrequency,
        new Date(client.periodStartDate).toLocaleDateString(),
        new Date(client.periodEndDate).toLocaleDateString(),
        new Date(client.dueDate).toLocaleDateString(),
        client.status,
        client.employeeAssigned || '',
        client.employeeEmail || '',
      ]);

      const csv = [
        headers.join(','),
        ...rows.map((row: any[]) => 
          row.map((cell: any) => `"${cell}"`).join(',')
        ),
      ].join('\n');

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Clients report downloaded successfully!');
    } catch (err) {
      setError('Failed to download clients report');
    } finally {
      setLoading(false);
    }
  };

  const downloadFilingsReport = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await filingApi.getAll();
      const filings = response.data.data || [];

      // Convert to CSV
      const headers = [
        'Client Name',
        'Client Email',
        'Period Start',
        'Period End',
        'Due Date',
        'Status',
        'Filed Date',
        'Notes',
        'Employee Assigned',
      ];

      const rows = filings.map((filing: any) => [
        filing.client?.clientName || '',
        filing.client?.clientEmail || '',
        new Date(filing.periodStartDate).toLocaleDateString(),
        new Date(filing.periodEndDate).toLocaleDateString(),
        new Date(filing.dueDate).toLocaleDateString(),
        filing.status,
        filing.filedDate ? new Date(filing.filedDate).toLocaleDateString() : '',
        filing.notes || '',
        filing.client?.employeeAssigned || '',
      ]);

      const csv = [
        headers.join(','),
        ...rows.map((row: any[]) => 
          row.map((cell: any) => `"${cell}"`).join(',')
        ),
      ].join('\n');

      // Download CSV
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `filings_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      setSuccess('Filings report downloaded successfully!');
    } catch (err) {
      setError('Failed to download filings report');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, fontSize: '2rem', mb: 1, color: '#1a1a1a' }}
        >
          Reports
        </Typography>
        <Typography variant="body1" sx={{ color: '#666666', fontSize: '1rem' }}>
          Download reports for clients and filings
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 3,
        }}
      >
        {/* Clients Report Card */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid #E5E7EB',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#667eea',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Description sx={{ fontSize: 32, color: '#FFFFFF' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 1, color: '#111827' }}
            >
              Clients Report
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#6B7280', fontSize: '0.875rem', mb: 3 }}
            >
              Download a complete list of all clients with their details, status, and
              assigned employees
            </Typography>
            <Button
              startIcon={<Download />}
              onClick={downloadClientsReport}
              disabled={loading}
              fullWidth
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
                },
                '&:disabled': {
                  background: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Download Clients Report'}
            </Button>
          </CardContent>
        </Card>

        {/* Filings Report Card */}
        <Card
          elevation={0}
          sx={{
            border: '1px solid #E5E7EB',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#667eea',
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
              }}
            >
              <Assignment sx={{ fontSize: 32, color: '#FFFFFF' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.25rem', mb: 1, color: '#111827' }}
            >
              Filings Report
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#6B7280', fontSize: '0.875rem', mb: 3 }}
            >
              Download a complete list of all filings with periods, due dates, status,
              and assigned employees
            </Typography>
            <Button
              startIcon={<Download />}
              onClick={downloadFilingsReport}
              disabled={loading}
              fullWidth
              sx={{
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                px: 3,
                py: 1.25,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
                },
                '&:disabled': {
                  background: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Download Filings Report'}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Reports;